import DownloadIcon from '@mui/icons-material/Download';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import ProductBorderImage from '/@/components/catalog/product-border-image';
import DKProtectLogo from '/@/components/dk-protect/assets/logo.svg';
import IconText from '/@/components/icon-text';
import Pills from '/@/components/pills';
import ShareButtons from '/@/components/share-buttons';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { useCountry } from '/@/hooks/nitrogen-smart';
import { _, getActiveLanguage } from '/@/locale';
import { generateCatalogPDF } from '/@/store/catalog/apiProviders';
import { getCatalogComparisons } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { withOverrideTheme } from '/@/utils/theme-override';
import Title from '/@/v3/components/title';
import styles from '../styles.module.css';

const Description = ({ product, scroll, type, itemId, zoneId, slug }) => {
    const isMobile = useIsMobile();
    const sessionCountry = useSelector(getSessionCountry);
    const typeForPills = (product.type && product.type.name) || product.type;
    const isCropProtection = type === 'crop_protection';
    const notes = isCropProtection
        ? product.valueProposal.map((vp) => _(vp.text))
        : product.notes.map((note) => _(note.text));
    const shortDescription = !isCropProtection ? _(product.shortDescription) : _(product.shortDescription);
    const name = !isCropProtection ? product.catalogName : product.name;
    const DK = !isCropProtection ? product.technologies && product.technologies.name === 'DKProtect' : {};
    const products = { ...product, DK };
    let DKProtect =
        products.technologies &&
        products.technologies.map((tec) => {
            return tec.codename && tec.codename;
        });
    DKProtect = DKProtect && DKProtect.includes('DKProtect');
    const enableDKProtect = Boolean(DKProtect);
    const description = !isCropProtection ? '' : _(product.description);
    const isSoyBR = type === 'soy' && sessionCountry.code === 'BR';
    const comparisons = useSelector(getCatalogComparisons);
    const country = useCountry();
    const language = getActiveLanguage();

    const shareUrl = withOverrideTheme(
        `${window.location.href.split('?')[0]}?country=${sessionCountry.code}&comparisons=${comparisons.join(',')}`
    );

    const shareButtons = (
        <ShareButtons
            message={`${_('Te comparto un catalogo de {{appName}}!', {
                appName: settings.app.name,
            })} \n ${shareUrl}`}
            handleCopy={() => {
                navigator.clipboard.writeText(shareUrl);
            }}
            handleDownload={() =>
                generateCatalogPDF({
                    comparisonsIds: comparisons,
                    type,
                    itemId,
                    zoneId,
                    slug,
                    country: country.code,
                    language,
                    fileName: `${_('Catálogo')} - ${product.name}.pdf`,
                })
            }
        />
    );

    const notesForRender = (
        <Grid item xs={12}>
            <Grid container direction='column' spacing={2}>
                {notes.map((note) => {
                    return (
                        <Grid key={note.es} item xs={12}>
                            <IconText bigFontSize translate text={note} withTheme={false} />
                        </Grid>
                    );
                })}
            </Grid>
        </Grid>
    );

    return (
        <Box
            sx={{
                p: 4,
                height: '100%',
                border: `1px solid ${colors.gray[800]}`,
                borderRadius: 2,
            }}
        >
            <Grid container direction={isCropProtection ? 'row' : 'column'}>
                <Grid item xs={isCropProtection && !isMobile ? 9 : 12} sx={{ mb: 2 }}>
                    <Grid container direction='row' spacing={2}>
                        <Grid item xs={enableDKProtect ? 11 : 12}>
                            <Grid container direction='row' spacing={2}>
                                {(!isCropProtection || (isCropProtection && (!scroll || !isMobile))) && (
                                    <Grid item xs={12} md={3}>
                                        <Title size='xl' italic bolder>
                                            {name}
                                        </Title>
                                    </Grid>
                                )}

                                {isCropProtection && (!scroll || !isMobile) && (
                                    <Grid item>
                                        <Pills type={typeForPills} crops={product?.crops} />
                                    </Grid>
                                )}

                                <Grid item xs={isCropProtection ? 8 : 12}>
                                    <Title size='md' italic bolder theme={settings.app.theme}>
                                        {shortDescription}
                                    </Title>
                                </Grid>
                                {isMobile && isCropProtection && !scroll && (
                                    <Grid
                                        sx={{
                                            position: { xs: 'absolute', md: 'relative' },
                                            top: { xs: 11, md: -45 },
                                            right: 58,
                                        }}
                                        container
                                        justifyContent='center'
                                        item
                                        xs={4}
                                    >
                                        <ProductBorderImage
                                            srcImage={product.image}
                                            classNameLogoContainer={styles.classLogoContainer}
                                            classNameLogo={styles.classLogo}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {isCropProtection && (
                            <Fragment>
                                {description !== '' && (
                                    <Grid item xs={12}>
                                        <Title size='md'>{description}</Title>
                                    </Grid>
                                )}
                                {!isSoyBR && notes.length > 0 && notesForRender}
                            </Fragment>
                        )}
                        {enableDKProtect && (
                            <Grid
                                item
                                xs={1}
                                sx={{
                                    backgroundImage: `url(${DKProtectLogo})`,
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
                {!isSoyBR && notes.length > 0 && !isCropProtection && notesForRender}
                {isCropProtection && (
                    <Grid item xs={isCropProtection && !isMobile ? 3 : 12}>
                        <Grid container direction='column' justifyContent='center' alignItems='space-between'>
                            {!isMobile && (
                                <Grid
                                    sx={{
                                        position: { xs: 'absolute', md: 'relative' },
                                        top: { xs: 11, md: -45 },
                                        right: 58,
                                    }}
                                    container
                                    justifyContent='center'
                                    item
                                    xs={12}
                                >
                                    <ProductBorderImage
                                        srcImage={product.image}
                                        classNameLogoContainer={styles.classLogoContainer}
                                        classNameLogo={styles.classLogo}
                                    />
                                </Grid>
                            )}
                            {!isMobile && (
                                <Grid item xs={12}>
                                    <Grid container justifyContent='center' alignItems='center'>
                                        <Grid container justifyContent='center' item xs={4} sm={12}>
                                            {shareButtons}
                                        </Grid>
                                        {isCropProtection &&
                                            product.document.length > 0 &&
                                            product.document.map((docu) => {
                                                return (
                                                    <Grid key={docu.name} item xs={8} sm={12} sx={{ m: 1 }}>
                                                        <a href={docu.file} target='_blank' rel='noreferrer'>
                                                            <Button
                                                                variant='contained'
                                                                fullWidth
                                                                sx={{ mt: 2 }}
                                                                endIcon={<DownloadIcon />}
                                                            >
                                                                {_(docu.name)}
                                                            </Button>
                                                        </a>
                                                    </Grid>
                                                );
                                            })}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
                {!isCropProtection && (
                    <Grid container justifyContent='flex-start' item xs={12} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                            {shareButtons}
                        </Grid>
                    </Grid>
                )}
                {isCropProtection && isMobile && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Grid container direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
                            <Grid item container justifyContent='flex-start' xs={12} md={3}>
                                {shareButtons}
                            </Grid>
                            {product.document.length > 0 &&
                                product.document.map((document) => {
                                    return (
                                        <Grid key={document.name} item xs={12} sm={4}>
                                            <a href={document.file} target='_blank' rel='noreferrer'>
                                                <Button sx={{ color: colors.gray[700] }} endIcon={<DownloadIcon />}>
                                                    {_(document.name)}
                                                </Button>
                                            </a>
                                        </Grid>
                                    );
                                })}
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default Description;
